import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Mail, Phone, Clock, MapPin } from "lucide-react";
import { FIRM, SERVICES, LOGO_URL, POSTAL, PRACTICE_CONTACTS } from "@/lib/content";

export default function Footer() {
  const exploreLinks = [
    { label: "Home", to: "/" },
    { label: "About", to: "/about" },
    { label: "Services", to: "/services" },
    { label: "Contact", to: "/contact" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="border-t border-sage-100 bg-sage-50/70"
    >
      <div className="container-narrow py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          {/* Logo + postal + hours */}
          <div className="md:col-span-5">
            <img src={LOGO_URL} alt="Bellhouse Law" className="h-40 md:h-48 w-auto" />

            <div className="mt-8 space-y-5 text-sm text-ink-soft">
              <div className="flex items-start gap-3" data-testid="footer-postal">
                <MapPin className="mt-0.5 h-4 w-4" strokeWidth={1.5} />
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-muted">
                    Postal Address
                  </div>
                  <div className="mt-1 text-ink">
                    {POSTAL.line1}
                    <br />
                    {POSTAL.line2}
                    <br />
                    {POSTAL.country}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="footer-hours">
                <Clock className="mt-0.5 h-4 w-4" strokeWidth={1.5} />
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-muted">
                    Hours
                  </div>
                  <div className="mt-1 text-ink">{FIRM.hours}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Practice area contacts */}
          <div className="md:col-span-7 grid grid-cols-1 gap-8 sm:grid-cols-2">
            {[PRACTICE_CONTACTS.commercial, PRACTICE_CONTACTS.residential].map(
              (c) => (
                <div
                  key={c.email}
                  data-testid={`footer-contact-${c.email.split("@")[0]}`}
                >
                  <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-sage-700">
                    <div>{c.area}</div>
                    <div>Enquiries</div>
                  </div>
                  <div className="mt-4 text-base font-semibold text-ink">
                    {c.name}
                  </div>
                  <div className="mt-3 space-y-1.5 text-sm text-ink-soft">
                    <a
                      href={`tel:${c.phone.replace(/\s/g, "")}`}
                      className="flex items-center gap-2 hover:text-sage-700"
                    >
                      <Phone className="h-3.5 w-3.5" strokeWidth={1.5} />
                      {c.phone}
                    </a>
                    <a
                      href={`mailto:${c.email}`}
                      className="flex items-center gap-2 hover:text-sage-700"
                    >
                      <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
                      {c.email}
                    </a>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>

        {/* Secondary nav */}
        <div className="mt-16 grid grid-cols-1 gap-12 border-t border-sage-100 pt-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="eyebrow">Explore</div>
            <ul className="mt-6 space-y-3 text-sm">
              {exploreLinks.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}
                    className="text-ink-soft hover:text-sage-700"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-8">
            <div className="eyebrow">Practice Areas</div>
            <ul className="mt-6 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-ink-soft hover:text-sage-700"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-make-enquiry"
                  className="text-ink-soft hover:text-sage-700"
                >
                  Make an Enquiry
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-sage-100 pt-8 md:flex-row md:items-center">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-ink-muted">
            © {new Date().getFullYear()} Bellhouse Law. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-ink-soft">
            <a
              href={FIRM.social.linkedin}
              aria-label="LinkedIn"
              data-testid="social-linkedin"
              className="hover:text-sage-700"
            >
              <Linkedin className="h-4 w-4" strokeWidth={1.5} />
            </a>
            <a
              href={FIRM.social.instagram}
              aria-label="Instagram"
              data-testid="social-instagram"
              className="hover:text-sage-700"
            >
              <Instagram className="h-4 w-4" strokeWidth={1.5} />
            </a>
            <a
              href={FIRM.social.facebook}
              aria-label="Facebook"
              data-testid="social-facebook"
              className="hover:text-sage-700"
            >
              <Facebook className="h-4 w-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
